import * as React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { colorSchemes, typography } from './themePrimitives';

function AppTheme(props) {
  const { children } = props;
  const [mode, setMode] = React.useState(() => {
    // Check localStorage or system preference
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) return savedMode;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Listen for system theme changes
  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const theme = React.useMemo(() => createTheme({
    cssVariables: {
      colorSchemeSelector: 'data-mui-color-scheme',
      cssVarPrefix: 'ogloszenia',
    },
    colorSchemes,
    typography,
    shape: {
      borderRadius: 14,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (themeParam) => ({
          html: {
            scrollBehavior: 'smooth',
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
          body: {
            margin: 0,
            overflowX: 'hidden',
          },
          '*:focus-visible': {
            outline: `2px solid ${themeParam.palette.primary.main}`,
            outlineOffset: 2,
          },
          'button, a': {
            WebkitTapHighlightColor: 'transparent',
          },
        }),
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            fontWeight: 500,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            transition: 'box-shadow 200ms ease, border-color 200ms ease, background-color 200ms ease',
            '&:hover': {
              boxShadow: 6,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 16,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
    },
  }), []);

  // Set color scheme
  React.useEffect(() => {
    document.documentElement.setAttribute('data-mui-color-scheme', mode);
    localStorage.setItem('themeMode', mode);
  }, [mode]);
  
  return (
    <ThemeProvider theme={theme} defaultMode={mode}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
}

AppTheme.propTypes = {
  children: PropTypes.node,
};

export default AppTheme;